import { formatOfficerDate, formatOfficerValue } from '../../lib/officerFormat.js'

/** One record-side blank an acceptance would fill (decision 60; the API's
 *  `EvidenceDecisionPreview.record_writes`, `PlannedRecordWrite` in
 *  src/nolos/policy/evidence_decision_schemas.py). A plan, not a receipt:
 *  attribution and skip reasons only exist once the follow-on writer ran. */
export interface PlannedRecordWrite {
  target_type: 'trade' | 'position'
  target_id: string
  field: 'settlement_date' | 'units_held' | 'balance_as_of'
  /** ISO date for the date fields; the plain figure ("1200") for a unit count. */
  value: string
  source: { document_id?: string; asserted_field?: string; check_id?: string }
  detail: string
  /** units fill only: the date the stated count is true as of. */
  as_of_date?: string | null
  /** checkpoint advance only: the agreeing figure the record already states. */
  units_held?: string | null
}

const date = (iso: string) => formatOfficerDate(iso) ?? iso

/** The one-line notice for a planned write, in officer words:
 *  "settlement date 10 Apr 2025 from S13",
 *  "unit count 1,200 as of 6 Mar 2025 from S13",
 *  "balance date 15 Apr 2025 from S13". */
export function recordWriteLine(write: PlannedRecordWrite, documentTitle: string): string {
  let what: string
  switch (write.field) {
    case 'settlement_date':
      what = `settlement date ${date(write.value)}`
      break
    case 'units_held':
      what = `unit count ${formatOfficerValue(write.value)}`
      if (write.as_of_date) what += ` as of ${date(write.as_of_date)}`
      break
    case 'balance_as_of':
      what = `balance date ${date(write.value)}`
      break
    default:
      what = `${String(write.field).replace(/_/g, ' ')} ${formatOfficerValue(write.value)}`
  }
  return `${what} from ${documentTitle}`
}
